export type FaqItem = {
  q: { en: string; ja: string };
  a: { en: string; ja: string };
};

export type FaqSection = {
  title: { en: string; ja: string };
  items: FaqItem[];
};

export const FAQ_CHLOLODESIGNS: FaqSection[] = [
  {
    title: { en: "About Our Cards", ja: "カードについて" },
    items: [
      {
        q: { en: "What materials are your cards made from?", ja: "カードはどのような素材で作られていますか？" },
        a: {
          en: "Each card is printed on premium fine-art paper using archival pigment inks on our Epson SC-PX1V professional printer. The result is vivid, long-lasting colour with a beautiful matte finish.",
          ja: "各カードは、Epson SC-PX1Vプロフェッショナルプリンターを使用し、アーカイバル顔料インクで高品質なファインアート用紙に印刷されています。鮮やかで長持ちする色彩と美しいマット仕上げが特徴です。",
        },
      },
      {
        q: { en: "What sizes do you offer?", ja: "どのサイズがありますか？" },
        a: {
          en: "We offer A6 greeting cards (with envelope), as well as A4, A3, and A2 art prints. Size-specific pricing is shown on each product page.",
          ja: "A6グリーティングカード（封筒付き）、A4、A3、A2のアートプリントをご用意しています。サイズ別の価格は各商品ページに記載されています。",
        },
      },
      {
        q: { en: "Do A6 cards come with an envelope?", ja: "A6カードには封筒が付いていますか？" },
        a: {
          en: "Yes, all A6 greeting cards include a matching envelope. Larger print sizes (A4, A3, A2) are sold as prints only.",
          ja: "はい、すべてのA6グリーティングカードにはマッチした封筒が付属しています。大きめのサイズ（A4・A3・A2）はプリントのみの販売となります。",
        },
      },
      {
        q: { en: "Can I order a single card?", ja: "1枚から注文できますか？" },
        a: {
          en: "Yes! All items are available as single-unit orders — no minimum quantity required.",
          ja: "はい！すべての商品は1枚から注文可能です。最低注文数量はありません。",
        },
      },
    ],
  },
  {
    title: { en: "Ordering", ja: "ご注文について" },
    items: [
      {
        q: { en: "How do I place an order?", ja: "注文方法を教えてください。" },
        a: {
          en: "Browse our shop, add items to your cart, and complete checkout. We accept credit/debit cards via Stripe and PayPal. Guest checkout is available — no account required.",
          ja: "ショップを閲覧し、商品をカートに追加してチェックアウトを完了してください。StripeとPayPalでお支払いいただけます。ゲストチェックアウトも可能で、アカウント登録は不要です。",
        },
      },
      {
        q: { en: "How long does production take?", ja: "制作にはどれくらいかかりますか？" },
        a: {
          en: "Orders are printed and dispatched within 3 business days. You will receive a shipping notification with a tracking number once your order is on its way.",
          ja: "ご注文は3営業日以内に印刷・発送されます。発送後、追跡番号を記載した発送通知をお送りします。",
        },
      },
      {
        q: { en: "Can I cancel my order?", ja: "注文をキャンセルできますか？" },
        a: {
          en: "Orders with a status of \"Not Paid\" or \"Paid\" can be cancelled from your account page. Once production has started (status: Shipped), cancellations are no longer possible.",
          ja: "「未支払い」または「支払い済み」のステータスの注文は、アカウントページからキャンセルできます。制作が開始された後（ステータス：発送済み）はキャンセルできません。",
        },
      },
    ],
  },
  {
    title: { en: "Shipping & Delivery", ja: "配送について" },
    items: [
      {
        q: { en: "Do you ship internationally?", ja: "海外発送は対応していますか？" },
        a: {
          en: "Currently we ship within Japan only. International shipping is something we are working towards — please check back soon.",
          ja: "現在、日本国内への発送のみ対応しています。国際発送については現在検討中ですので、今後の情報にご期待ください。",
        },
      },
      {
        q: { en: "How long does delivery take?", ja: "配送にはどれくらいかかりますか？" },
        a: {
          en: "After dispatch, delivery typically takes 1–3 business days within Japan, depending on your location.",
          ja: "発送後、お住まいの地域によりますが、通常1〜3営業日でお届けします。",
        },
      },
      {
        q: { en: "Will I receive a tracking number?", ja: "追跡番号はもらえますか？" },
        a: {
          en: "Yes. A tracking number is included in the shipping notification email sent when your order is dispatched.",
          ja: "はい。ご注文の発送時に送信される発送通知メールに追跡番号が記載されています。",
        },
      },
    ],
  },
  {
    title: { en: "Payment", ja: "お支払いについて" },
    items: [
      {
        q: { en: "What payment methods do you accept?", ja: "どの支払い方法が使えますか？" },
        a: {
          en: "We accept major credit and debit cards via Stripe, and PayPal.",
          ja: "Stripe経由の主要なクレジット・デビットカード、およびPayPalをご利用いただけます。",
        },
      },
      {
        q: { en: "Do prices include tax?", ja: "価格に税金は含まれていますか？" },
        a: {
          en: "Yes. All displayed prices include Japanese consumption tax (10%).",
          ja: "はい。表示価格にはすべて日本の消費税（10%）が含まれています。",
        },
      },
    ],
  },
  {
    title: { en: "Returns & Replacements", ja: "返品・交換について" },
    items: [
      {
        q: { en: "What if my order arrives damaged?", ja: "注文品が破損して届いた場合はどうすればいいですか？" },
        a: {
          en: "If your order arrives damaged or defective, please contact us within 14 days of delivery with a photo of the damage. We will reprint or issue a full refund at no extra cost.",
          ja: "注文品が破損または不良品で届いた場合は、配送から14日以内に損傷の写真を添えてご連絡ください。追加費用なしで再印刷または全額返金いたします。",
        },
      },
      {
        q: { en: "Can I exchange for a different design?", ja: "別のデザインに交換できますか？" },
        a: {
          en: "We do not offer design exchanges, but you are welcome to place a new order for a different item.",
          ja: "デザイン交換には対応しておりませんが、別の商品を新たにご注文いただくことは可能です。",
        },
      },
      {
        q: { en: "How do I contact you?", ja: "どのように連絡すればいいですか？" },
        a: {
          en: "You can reach us at support@chlolodesigns.com. We aim to respond within 1–2 business days.",
          ja: "support@chlolodesigns.comまでご連絡ください。1〜2営業日以内にご返信いたします。",
        },
      },
    ],
  },
];

export const FAQ_CHLORINE: FaqSection[] = [
  {
    title: { en: "About Our Prints", ja: "プリントについて" },
    items: [
      {
        q: { en: "What printing method do you use?", ja: "どのような印刷方法を使用していますか？" },
        a: {
          en: "We use giclée printing on an Epson SC-PX1V with 10-colour UltraChrome Pro10 archival pigment inks — the same standard used by fine-art galleries and museums worldwide.",
          ja: "Epson SC-PX1Vと10色のUltraChrome Pro10アーカイバル顔料インクを使用したジークレープリントを採用しています。これは世界中のファインアートギャラリーや美術館で使用されているのと同じ基準です。",
        },
      },
      {
        q: { en: "What paper do you use?", ja: "どのような用紙を使用していますか？" },
        a: {
          en: "We use museum-grade archival media selected for exceptional colour accuracy, tonal depth, and longevity. Specific paper information is listed on each product page.",
          ja: "優れた色精度、階調の深さ、耐久性を追求して選ばれたミュージアムグレードのアーカイバル用紙を使用しています。各商品ページに具体的な用紙情報が記載されています。",
        },
      },
      {
        q: { en: "What sizes are available?", ja: "どのサイズがありますか？" },
        a: {
          en: "We offer A4, A3, and A2 prints. Custom sizes may be available on request — please contact us at support@chlorine.co.jp.",
          ja: "A4、A3、A2のプリントをご用意しています。カスタムサイズについてはご相談ください（support@chlorine.co.jp）。",
        },
      },
      {
        q: { en: "How long will the prints last?", ja: "プリントはどれくらい長持ちしますか？" },
        a: {
          en: "Our archival pigment prints are rated for 100+ years under proper display conditions (out of direct sunlight, behind UV-protective glass).",
          ja: "当社のアーカイバル顔料プリントは、適切な展示条件（直射日光を避け、UV保護ガラスの後ろに設置）のもとで100年以上の耐久性があります。",
        },
      },
      {
        q: { en: "Do you offer framing?", ja: "額装サービスはありますか？" },
        a: {
          en: "We do not currently offer framing. Our prints use standard sizes (A4, A3, A2) that fit most off-the-shelf frames available at home goods and art supply stores.",
          ja: "現在、額装サービスは提供していません。当社のプリントは標準サイズ（A4、A3、A2）を使用しており、ホームセンターや画材店で販売されている既製品のフレームに対応しています。",
        },
      },
    ],
  },
  {
    title: { en: "Ordering", ja: "ご注文について" },
    items: [
      {
        q: { en: "Can I order a single print?", ja: "1枚から注文できますか？" },
        a: {
          en: "Yes. All prints are available as single-unit orders with no minimum quantity.",
          ja: "はい。すべてのプリントは最低注文数量なしで1枚から注文可能です。",
        },
      },
      {
        q: { en: "How long does production take?", ja: "制作にはどれくらいかかりますか？" },
        a: {
          en: "Prints are produced and dispatched within 3 business days. Each piece is inspected before packaging to ensure it meets our quality standards.",
          ja: "プリントは3営業日以内に制作・発送されます。梱包前に各作品を検査し、品質基準を満たしていることを確認しています。",
        },
      },
      {
        q: { en: "Can I request a custom size?", ja: "カスタムサイズを注文できますか？" },
        a: {
          en: "Yes, custom sizing is available on request. Please contact us at support@chlorine.co.jp with the dimensions and artwork you have in mind.",
          ja: "はい、カスタムサイズはリクエストにより対応可能です。ご希望のサイズと作品についてsupport@chlorine.co.jpまでご連絡ください。",
        },
      },
      {
        q: { en: "Can I cancel my order?", ja: "注文をキャンセルできますか？" },
        a: {
          en: "Orders with a status of \"Not Paid\" or \"Paid\" can be cancelled from your account page. Once production has started (status: Shipped), cancellation is no longer possible.",
          ja: "「未支払い」または「支払い済み」のステータスの注文は、アカウントページからキャンセルできます。制作が開始された後（ステータス：発送済み）はキャンセルできません。",
        },
      },
    ],
  },
  {
    title: { en: "Shipping & Delivery", ja: "配送について" },
    items: [
      {
        q: { en: "Do you ship internationally?", ja: "海外発送は対応していますか？" },
        a: {
          en: "Currently we ship within Japan only. We are working towards international shipping — please check back soon.",
          ja: "現在、日本国内への発送のみ対応しています。国際発送については現在検討中ですので、今後の情報にご期待ください。",
        },
      },
      {
        q: { en: "How long does delivery take?", ja: "配送にはどれくらいかかりますか？" },
        a: {
          en: "After dispatch, delivery typically takes 1–3 business days within Japan depending on your location.",
          ja: "発送後、お住まいの地域によりますが、通常1〜3営業日でお届けします。",
        },
      },
      {
        q: { en: "Will I receive a tracking number?", ja: "追跡番号はもらえますか？" },
        a: {
          en: "Yes. A tracking number is included in the shipping notification email sent when your order is dispatched.",
          ja: "はい。ご注文の発送時に送信される発送通知メールに追跡番号が記載されています。",
        },
      },
    ],
  },
  {
    title: { en: "Payment", ja: "お支払いについて" },
    items: [
      {
        q: { en: "What payment methods do you accept?", ja: "どの支払い方法が使えますか？" },
        a: {
          en: "We accept major credit and debit cards via Stripe, and PayPal.",
          ja: "Stripe経由の主要なクレジット・デビットカード、およびPayPalをご利用いただけます。",
        },
      },
      {
        q: { en: "Do prices include tax?", ja: "価格に税金は含まれていますか？" },
        a: {
          en: "Yes. All displayed prices include Japanese consumption tax (10%).",
          ja: "はい。表示価格にはすべて日本の消費税（10%）が含まれています。",
        },
      },
    ],
  },
  {
    title: { en: "Returns & Replacements", ja: "返品・交換について" },
    items: [
      {
        q: { en: "What if my print arrives damaged?", ja: "プリントが破損して届いた場合はどうすればいいですか？" },
        a: {
          en: "If your print arrives damaged or defective, please contact us within 14 days with a photo. We will reprint or issue a full refund at no extra cost.",
          ja: "プリントが破損または不良品で届いた場合は、14日以内に写真を添えてご連絡ください。追加費用なしで再印刷または全額返金いたします。",
        },
      },
      {
        q: { en: "How do I contact you?", ja: "どのように連絡すればいいですか？" },
        a: {
          en: "You can reach us at support@chlorine.co.jp. We aim to respond within 1–2 business days.",
          ja: "support@chlorine.co.jpまでご連絡ください。1〜2営業日以内にご返信いたします。",
        },
      },
    ],
  },
];
